// The History drawer of the player page (spec/dashboard.md section 11.2; spec/api-conventions.md section 16.2): the versions
// newest first with Load more (keyset paging), for the whole player, a collection or one key; a row opens its value with
// the diff against the current value or the key's previous version, and offers Restore…; "Restore … to an instant…" in the
// head. The filter row (section 11.2 History filters): Range chips (All · 24 h · 7 d · 30 d · Custom… with From / To) → the
// query's `from` / `to`, Source → `source` (spec/api-conventions.md section 16.2), both in the URL (`since=`, `from=`, `to=`,
// `source=`) with the drawer's `collection` / `key`; Actor filters the loaded pages client-side (the API has none).
import { useEffect, useMemo, useState } from 'react';
import { ops } from '../../api/operations';
import type { Json, Page, Player, SaveDiff, SaveVersion } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { formatBytesShort } from '../../players/PlayerTree';
import { playerPrefix } from '../../players/usePlayer';
import { useGame } from '../../shell/GameContext';
import { Button } from '../../ui/Button';
import { Badge, type Tone } from '../../ui/Chip';
import { Modal } from '../../ui/Modal';
import { formatInstant, useTimeZone } from '../../ui/time';
import { useQuery } from '../../workspace/cache';
import { HISTORY_SINCE, SOURCE_LABELS, errorText } from './commands';
import { LoadError, SkeletonRows } from '../../ui/PageStates';
import { History } from '../../ui/icons';
import { Tabs } from '../../ui/Tabs';
import { Select, TextInput } from '../../ui/inputs';
import { DateTimeInput } from '../../ui/values';

export const SOURCE_TONE: Record<string, Tone> = { client: 'neutral', admin: 'accent', restore: 'warning', reset: 'warning', import: 'info', delete: 'danger' };

const PAGE_SIZE = 50;

export interface HistoryFilter {
  collection?: string;
  key?: string;
  /** a relative range (`24h`, `7d`, `30d`): `from` = now − the span when the page is read */
  since?: string;
  /** a custom range, ISO instants (`from` inclusive, `to` exclusive) */
  from?: string;
  to?: string;
  source?: string;
}

/** The drawer's URL params, in the order PlayerPage writes them. */
export const HISTORY_PARAMS = ['collection', 'key', 'since', 'from', 'to', 'source'] as const;

const toSeconds = (iso: string | undefined) => (iso ? Math.floor(Date.parse(iso) / 1000) : null);
const toIso = (seconds: number | null) => (seconds === null ? undefined : new Date(seconds * 1000).toISOString());

export interface HistoryDrawerProps {
  env: string;
  player: Player;
  open: boolean;
  filter: HistoryFilter;
  onClose: () => void;
  /** widens the filter (the "Whole player" button) */
  onFilter: (filter: HistoryFilter) => void;
  writeMissing: string | null;
  onRestore: (version: SaveVersion) => void;
  onRestoreAt: (collection?: string) => void;
}

export function HistoryDrawer({ env, player, open, filter, onClose, onFilter, writeMissing, onRestore, onRestoreAt }: HistoryDrawerProps) {
  const title = filter.collection ? `History · ${filter.collection}${filter.key ? `/${filter.key}` : ''}` : 'History · the whole player';
  return (
    <Modal title={title} open={open} onClose={onClose} drawer size="l" className="history-drawer-modal">
      {open && <HistoryBody env={env} player={player} filter={filter} onFilter={onFilter} writeMissing={writeMissing} onRestore={onRestore} onRestoreAt={onRestoreAt} />}
    </Modal>
  );
}

function HistoryBody({ env, player, filter, onFilter, writeMissing, onRestore, onRestoreAt }: Omit<HistoryDrawerProps, 'open' | 'onClose'>) {
  const { client } = useAuth();
  const { org, game, offsets } = useGame();
  const [zone] = useTimeZone();
  const path = { org, game: game.slug, env, player: player.id };
  const prefix = playerPrefix(game.id, env, player.id);
  const since = filter.since && HISTORY_SINCE[filter.since] ? filter.since : undefined;
  const source = filter.source && (SOURCE_LABELS as readonly string[]).includes(filter.source) ? filter.source : undefined;
  const filterKey = [filter.collection ?? '', filter.key ?? '', since ?? '', since ? '' : (filter.from ?? ''), since ? '' : (filter.to ?? ''), source ?? ''].join('|');
  // the bounds are fixed per filter (a relative range anchors at the moment it is chosen) so Load more pages the same set
  const bounds = useMemo(() => (since ? { from: new Date(Date.now() - HISTORY_SINCE[since]!.ms).toISOString() } : { ...(filter.from ? { from: filter.from } : {}), ...(filter.to ? { to: filter.to } : {}) }), [filterKey]);
  const query = (cursor?: string) => ({
    ...(filter.collection ? { collection: filter.collection } : {}),
    ...(filter.key ? { key: filter.key } : {}),
    ...(source ? { source: source as SaveVersion['source'] } : {}),
    ...bounds,
    limit: PAGE_SIZE,
    ...(cursor ? { cursor } : {}),
  });
  const first = useQuery(`${prefix}:history:${filterKey}`, () => client.data(ops.listPlayerHistory, { path, query: query() }) as unknown as Promise<Page<SaveVersion>>);
  const [more, setMore] = useState<{ items: SaveVersion[]; cursor: string | null } | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [actor, setActor] = useState('');
  const [custom, setCustom] = useState(() => !since && !!(filter.from || filter.to));

  // a new first page (a filter change, a re-read after a command) drops the pages loaded after it
  useEffect(() => {
    setMore(null);
    setMoreError(null);
  }, [first.data, filterKey]);
  useEffect(() => setSelected(null), [filterKey]);

  const loaded = [...(first.data?.items ?? []), ...(more?.items ?? [])];
  const needle = actor.trim().toLowerCase();
  const rows = needle ? loaded.filter((v) => `${v.actor?.label ?? ''} ${v.actor?.id ?? ''}`.toLowerCase().includes(needle)) : loaded;
  const range = since ?? (custom ? 'custom' : 'all');
  const setRange = (k: string) => {
    if (k === 'custom') {
      setCustom(true);
      onFilter({ ...filter, since: undefined });
      return;
    }
    setCustom(false);
    onFilter({ ...filter, since: k === 'all' ? undefined : k, from: undefined, to: undefined });
  };
  const summary = [
    `${rows.length} version${rows.length === 1 ? '' : 's'}`,
    since ? HISTORY_SINCE[since]!.label : filter.from && filter.to ? `${formatInstant(toSeconds(filter.from)!, zone, { offsets })} – ${formatInstant(toSeconds(filter.to)!, zone, { offsets })}` : filter.from ? `since ${formatInstant(toSeconds(filter.from)!, zone, { offsets })}` : filter.to ? `before ${formatInstant(toSeconds(filter.to)!, zone, { offsets })}` : '',
    source ? `${source} writes` : '',
    needle ? `by “${actor.trim()}”` : '',
  ]
    .filter(Boolean)
    .join(' · ');
  const cursor = more ? more.cursor : (first.data?.nextCursor ?? null);
  const loadMore = async () => {
    if (!cursor) return;
    setLoadingMore(true);
    setMoreError(null);
    try {
      const page = (await client.data(ops.listPlayerHistory, { path, query: query(cursor) })) as unknown as Page<SaveVersion>;
      setMore((m) => ({ items: [...(m?.items ?? []), ...page.items], cursor: page.nextCursor }));
    } catch (e) {
      setMoreError(errorText(e));
    } finally {
      setLoadingMore(false);
    }
  };
  const selectedIndex = rows.findIndex((r) => r.id === selected);
  const selectedRow = selectedIndex >= 0 ? rows[selectedIndex]! : null;
  const previous = selectedRow ? (rows.slice(selectedIndex + 1).find((r) => r.collection === selectedRow.collection && r.key === selectedRow.key) ?? null) : null;

  return (
    <div className="history-drawer" data-testid="history-drawer">
      <div className="page-toolbar history-head">
        {filter.collection && (
          <Button size="s" variant="ghost" onClick={() => onFilter({ ...filter, collection: undefined, key: undefined })}>
            Whole player
          </Button>
        )}
        <span className="spacer" />
        <Button size="s" onClick={() => onRestoreAt(filter.collection)} missingPermission={writeMissing}>
          {filter.collection ? `Restore ${filter.collection} to an instant…` : 'Restore the player to an instant…'}
        </Button>
      </div>
      <div className="history-filters" role="group" aria-label="History filters" data-testid="history-filters">
        <div className="history-filter">
          <span className="field-label">Range</span>
          <Tabs
            segmented
            ariaLabel="Range"
            value={range}
            onChange={setRange}
            items={[
              { key: 'all', label: 'All' },
              { key: '24h', label: '24 h' },
              { key: '7d', label: '7 d' },
              { key: '30d', label: '30 d' },
              { key: 'custom', label: 'Custom…' },
            ]}
          />
        </div>
        {range === 'custom' && (
          <div className="history-filter history-custom-range">
            <span className="field-label">From</span>
            <DateTimeInput aria-label="From" value={toSeconds(filter.from)} allowEmpty onCommit={(s) => onFilter({ ...filter, since: undefined, from: toIso(s) })} ctx={{ offsets }} />
            <span className="field-label">To</span>
            <DateTimeInput aria-label="To" value={toSeconds(filter.to)} allowEmpty onCommit={(s) => onFilter({ ...filter, since: undefined, to: toIso(s) })} ctx={{ offsets }} />
          </div>
        )}
        <div className="history-filter">
          <span className="field-label">Source</span>
          <Select size="s" aria-label="Source" value={source ?? ''} onChange={(e) => onFilter({ ...filter, source: e.target.value || undefined })}>
            <option value="">All sources</option>
            {SOURCE_LABELS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Select>
        </div>
        <div className="history-filter">
          <span className="field-label">Actor</span>
          <TextInput size="s" type="search" aria-label="Actor" placeholder="Name or id" value={actor} onChange={(e) => setActor(e.target.value)} />
          <span className="muted small">on the loaded versions</span>
        </div>
      </div>
      {(first.data || rows.length > 0) && (
        <p className="history-summary muted small" data-testid="history-summary">
          {summary}
        </p>
      )}
      {first.error ? (
        <LoadError>{errorText(first.error)}</LoadError>
      ) : first.loading && !first.data ? (
        <SkeletonRows rows={4} label="Loading the versions" />
      ) : rows.length === 0 && loaded.length > 0 ? (
        <p className="muted small" data-testid="history-no-actor">
          No loaded version was written by “{actor.trim()}”.{cursor ? ' Load more to look further back.' : ''}
        </p>
      ) : rows.length === 0 ? (
        <div className="empty" data-testid="history-empty">
          <span className="empty-icon" aria-hidden="true">
            <History />
          </span>
          {since || filter.from || filter.to || source ? (
            <>
              <h3>No version matches these filters.</h3>
              <p>Widen the range or pick All sources.</p>
            </>
          ) : (
            <>
              <h3>No version recorded.</h3>
              <p>Every save of a key writes a version; restore any of them from here.</p>
            </>
          )}
        </div>
      ) : (
        <ul className="history-list" aria-label="Versions">
          {rows.map((v) => (
            <li key={v.id} data-testid={`version-${v.id}`} className={`history-row${v.id === selected ? ' selected' : ''}`} onClick={() => setSelected(v.id === selected ? null : v.id)}>
              <div className="history-row-main">
                <button type="button" className="linklike history-time" aria-expanded={v.id === selected} aria-label={`Open version ${v.version} of ${v.collection}/${v.key}`}>
                  {formatInstant(v.createdAt, zone, { offsets }, { seconds: true })}
                </button>
                <code className="history-path">
                  {v.collection}/{v.key}
                </code>
                <span className="history-version">v{String(v.version)}</span>
                <Badge tone={SOURCE_TONE[v.source] ?? 'neutral'}>{v.source}</Badge>
                {v.deleted && <Badge tone="danger">deleted</Badge>}
                <span className="spacer" />
                <span className="muted small">{formatBytesShort(v.bytes)}</span>
                <Button
                  size="s"
                  missingPermission={writeMissing}
                  onClick={(e) => {
                    e.stopPropagation();
                    onRestore(v);
                  }}
                >
                  Restore…
                </Button>
              </div>
              <div className="history-row-meta small">
                <span>{v.actor?.label}</span>
                {v.reason && <span className="muted"> · {v.reason}</span>}
              </div>
              {v.id === selected && (
                <div onClick={(e) => e.stopPropagation()}>
                  <VersionDetail env={env} player={player} version={v} previous={previous} />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {cursor && (
        <div className="row drawer-more">
          <Button size="s" onClick={() => void loadMore()} busy={loadingMore}>
            Load more
          </Button>
        </div>
      )}
      {moreError && (
        <LoadError>{moreError}</LoadError>
      )}
    </div>
  );
}

function VersionDetail({ env, player, version, previous }: { env: string; player: Player; version: SaveVersion; previous: SaveVersion | null }) {
  const { client } = useAuth();
  const { org, game } = useGame();
  const path = { org, game: game.slug, env, player: player.id, version: version.id };
  const prefix = playerPrefix(game.id, env, player.id);
  const valueQ = useQuery(`${prefix}:version:${version.id}`, () => client.data(ops.getPlayerVersion, { path }) as unknown as Promise<SaveVersion>);
  const [against, setAgainst] = useState<string | null>(null);
  const diffQ = useQuery(against ? `${prefix}:diff:${version.id}:${against}` : null, () => client.data(ops.diffPlayerVersion, { path, query: { against: against! } }) as unknown as Promise<SaveDiff>);
  return (
    <div className="version-detail" data-testid="version-detail">
      {valueQ.error ? (
        <LoadError>{errorText(valueQ.error)}</LoadError>
      ) : valueQ.loading && !valueQ.data ? (
        <SkeletonRows rows={2} label="Loading the value" />
      ) : valueQ.data?.deleted ? (
        <p className="muted small">A deletion: this version has no value.</p>
      ) : (
        <pre className="version-value" data-testid="version-value">
          {JSON.stringify(valueQ.data?.value ?? null, null, 2)}
        </pre>
      )}
      <div className="row version-diff-actions">
        <Button size="s" onClick={() => setAgainst('current')} variant={against === 'current' ? 'primary' : 'secondary'}>
          Diff against current
        </Button>
        <Button size="s" onClick={() => previous && setAgainst(previous.id)} disabled={!previous} title={previous ? `v${String(previous.version)}` : 'No older version of this key is loaded'} variant={against && against === previous?.id ? 'primary' : 'secondary'}>
          Diff against previous version
        </Button>
      </div>
      {against && <DiffTable loading={diffQ.loading && !diffQ.data} error={diffQ.error} diff={diffQ.data} />}
    </div>
  );
}

const show = (v: Json | null | undefined) => (v === undefined ? '—' : JSON.stringify(v));

function DiffTable({ loading, error, diff }: { loading: boolean; error: unknown; diff: SaveDiff | undefined }) {
  if (error)
    return (
      <LoadError>{errorText(error)}</LoadError>
    );
  if (loading || !diff) return <SkeletonRows rows={2} label="Comparing" />;
  if (diff.changes.length === 0)
    return (
      <p className="muted small" data-testid="version-diff">
        No field changed.
      </p>
    );
  return (
    <table className="diff version-diff" data-testid="version-diff">
      <thead>
        <tr>
          <th>Field</th>
          <th>Before</th>
          <th>After</th>
        </tr>
      </thead>
      <tbody>
        {diff.changes.map((c, i) => (
          <tr key={`${c.path}-${i}`}>
            <td>
              <code>{c.path}</code>
            </td>
            <td className="diff-before">
              <code>{show(c.before as Json)}</code>
            </td>
            <td className="diff-after">
              <code>{show(c.after as Json)}</code>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
